import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ExternalLink } from 'lucide-react';
import type { WidgetData } from '@auw/workspace-utils';

/**
 * URL of the sandbox shell document the widget <iframe> loads.
 *
 * It MUST be a real same-origin network URL (not `srcDoc`/`blob:`/`data:`):
 * those local-scheme documents inherit the embedder page's CSP and cannot
 * relax it, so under the strict CloudFront app CSP the widget's inline
 * bootstrap + CDN libraries get blocked and the frame renders blank. A
 * network document instead receives its OWN CSP (served by a dedicated
 * CloudFront cache behaviour with a permissive, widget-scoped policy +
 * `frame-ancestors 'self'`). See `public/widget-sandbox.html` and
 * `infra/terraform/.../askyourai_stack/main.tf`.
 */
// `v=` 는 셸 문서의 캐시 버스터 — widget-sandbox.html 의 부트스트랩 계약
// (idempotent set-content / ready·ACK 메시지 / storage shim)이 바뀔 때마다
// 올린다. 구버전 캐시 셸은 중복 set-content 를 그대로 재실행해 위젯
// 스크립트를 죽이므로 반드시 최신 셸이 로드되게 해야 한다.
const SANDBOX_URL = `${import.meta.env.BASE_URL}widget-sandbox.html?v=2`;

interface WidgetRendererProps {
  widget: WidgetData;
}

export default function WidgetRenderer({ widget }: WidgetRendererProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(Math.min(widget.height, 600));
  const [isLoaded, setIsLoaded] = useState(false);

  const handleMessage = useCallback(
    (e: MessageEvent) => {
      if (e.data?.type === 'widget-resize' && typeof e.data.height === 'number') {
        if (iframeRef.current && e.source === iframeRef.current.contentWindow) {
          const newHeight = Math.max(100, Math.min(e.data.height, 5000));
          setHeight(newHeight);
        }
      }
    },
    [],
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  const handleLoad = useCallback(() => setIsLoaded(true), []);

  // set-content 전송은 이 useEffect 단일 경로만 사용한다.
  // (과거 onLoad 핸들러 + useEffect 의 이중 전송이 sandbox 쪽 스크립트
  // 재실행을 유발 — 첫 실행이 만든 DOM 상태가 morphdom 에 의해 롤백되고
  // const/let 재선언 SyntaxError 로 위젯 인터랙션이 깨졌다.)
  useEffect(() => {
    if (!isLoaded || !iframeRef.current?.contentWindow) return;
    iframeRef.current.contentWindow.postMessage(
      {
        type: 'set-content',
        html: widget.widgetCode,
        runScripts: true,
        isSvg: widget.isSvg,
        title: widget.title.replace(/_/g, ' '),
      },
      '*',
    );
  }, [widget.widgetCode, widget.isSvg, widget.title, isLoaded]);

  // 새 창에서도 sandbox 셸 문서를 그대로 열고 postMessage 로 콘텐츠를
  // 주입한다. 과거 `window.open('') + document.write` 방식은
  // (a) </script> 치환이 위젯 인라인 스크립트의 닫는 태그까지 깨뜨려
  //     문서 전체 JS 가 죽고,
  // (b) about:blank 문서가 opener(앱)의 strict CSP 를 상속해 인라인
  //     스크립트가 차단되는 문제가 있었다. sandbox 셸 URL 은 자체 CSP
  //     를 가지므로 두 문제 모두 발생하지 않는다.
  const openInNewWindow = useCallback(() => {
    const win = window.open(SANDBOX_URL, '_blank');
    if (!win) return;
    const payload = {
      type: 'set-content',
      html: widget.widgetCode,
      runScripts: true,
      isSvg: widget.isSvg,
      title: widget.title.replace(/_/g, ' '),
    };
    // 새 창 문서의 message 리스너가 준비되기 전 전송은 유실되므로,
    // 적용 ACK('widget-content-applied')를 받을 때까지 짧게 폴링한다.
    // 셸의 set-content 처리는 멱등(동일 html 스킵)이라 중복 전송은 무해.
    let timer = 0;
    const cleanup = () => {
      window.clearInterval(timer);
      window.removeEventListener('message', onShellMessage);
    };
    const onShellMessage = (e: MessageEvent) => {
      if (e.source !== win) return;
      if (e.data?.type === 'widget-shell-ready') win.postMessage(payload, '*');
      if (e.data?.type === 'widget-content-applied') cleanup();
    };
    window.addEventListener('message', onShellMessage);
    let attempts = 0;
    timer = window.setInterval(() => {
      attempts += 1;
      if (win.closed || attempts > 40) {
        cleanup();
        return;
      }
      try {
        win.postMessage(payload, '*');
      } catch {
        /* window not ready yet */
      }
    }, 250);
  }, [widget.widgetCode, widget.isSvg, widget.title]);

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <HeaderIcon>{widget.isSvg ? '◇' : '⬡'}</HeaderIcon>
          <HeaderTitle>{widget.title.replace(/_/g, ' ')}</HeaderTitle>
        </HeaderLeft>
        <PopoutBtn onClick={openInNewWindow} title="Open in new window">
          <ExternalLink size={14} />
        </PopoutBtn>
      </Header>
      <IframeWrapper $height={height}>
        <StyledIframe
          ref={iframeRef}
          src={SANDBOX_URL}
          sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
          title={widget.title}
          onLoad={handleLoad}
          $height={height}
        />
      </IframeWrapper>
    </Container>
  );
}

const Container = styled.div`
  margin: 12px 0;
  border: 0.5px solid var(--color-border-tertiary, rgba(0, 0, 0, 0.15));
  border-radius: 12px;
  overflow: hidden;
  background: var(--color-bg-primary, #fff);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  border-bottom: 0.5px solid var(--color-border-tertiary, rgba(0, 0, 0, 0.1));
  background: var(--color-bg-secondary, #f5f5f5);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const HeaderIcon = styled.span`
  font-size: 13px;
  opacity: 0.6;
`;

const HeaderTitle = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-secondary, #666);
  text-transform: capitalize;
`;

const PopoutBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--color-text-tertiary, #999);
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: var(--color-text-primary, #1a1a1a);
    background: rgba(0, 0, 0, 0.06);
  }
`;

const IframeWrapper = styled.div<{ $height: number }>`
  width: 100%;
  height: ${(p) => p.$height}px;
  transition: height 0.2s ease;
  overflow: hidden;
`;

const StyledIframe = styled.iframe<{ $height: number }>`
  width: 100%;
  height: ${(p) => p.$height}px;
  border: none;
  display: block;
`;
